import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { getImageUrl } from "@/lib/api";
import { DEFAULT_PROJECTS, getStoredProjects } from "@/lib/projectsData";
import { Menu } from "@/components/Menu";
import { Footer } from "@/components/Footer";
import { useRevealObserver } from "@/hooks/useReveal";

const PORTRAIT = "/sebastian-ferradas.jpg";

const Line = ({ className = "" }) => (
  <div className={`line-reveal border-t border-[#111111] ${className}`} />
);

const HERO_BG_PATH = "/hero-bg.webp";

const Hero = () => {
  const heroBg = getImageUrl(HERO_BG_PATH);
  return (
    <section className="min-h-screen relative flex flex-col justify-center px-6 md:px-10 lg:px-16 pt-24 pb-16 overflow-hidden bg-[#111111] text-white" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <img
          src={heroBg}
          alt="Ferradás Arquitectura"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/10" />
      </div>

    <div className="relative z-10 [text-shadow:_0_8px_30px_rgba(0,0,0,0.1)]">
      <h1 className="reveal text-[17vw] md:text-[15vw] lg:text-[13vw] leading-[0.85] tracking-tighter font-medium uppercase text-white">
        Ferradás
      </h1>
      <div className="reveal mt-[-0.5em] md:ml-[28%] lg:ml-[35%] relative z-10" style={{ transitionDelay: "150ms" }}>
        <h2 className="text-[9vw] md:text-[6.5vw] leading-[0.95] tracking-tight font-light uppercase text-white">
          Arquitectura<br />Diseño<br />Gestión
        </h2>
      </div>
    </div>
    <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end relative z-10 [text-shadow:_0_6px_24px_rgba(0,0,0,0.1)]">
      <p className="reveal md:col-span-6 md:col-start-2 text-xl md:text-[2rem] font-light leading-snug text-white" style={{ transitionDelay: "300ms" }}>
        Arquitectura, diseño y gestión de proyectos para hotelería, centros comerciales y vivienda.
      </p>
      <div className="reveal md:col-span-3 md:col-start-9" style={{ transitionDelay: "400ms" }}>
        <Link
          to="/obras"
          data-testid="hero-cta-ver-obras"
          className="inline-flex items-center gap-3 text-lg uppercase tracking-wide font-medium text-white hover:text-[#D94A1E] hover:gap-6 transition-all duration-300"
        >
          Ver Obras <span aria-hidden>→</span>
        </Link>
      </div>
    </div>
    <div className="absolute bottom-8 left-6 md:left-10 lg:left-16 right-6 md:right-10 lg:right-16 z-10">
      <div className="line-reveal border-t border-white/40" />
    </div>
  </section>
);
};

const Intro = () => (
  <section className="px-6 md:px-10 lg:px-16 py-24 md:py-36" data-testid="intro-section">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5 reveal">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-8 text-[#D94A1E]">— El Estudio</p>
        <p className="text-2xl md:text-3xl lg:text-4xl leading-snug font-light">
          Ferradás es un estudio contemporáneo de arquitectura, diseño y gestión de proyectos con vocación internacional.
        </p>
        <p className="mt-8 text-base md:text-lg font-light leading-relaxed text-[#111111]/80 max-w-md">
          Trabajamos con precisión y rigor en cada etapa del proyecto: desde el concepto inicial hasta la supervisión de obra, construyendo espacios que perduran.
        </p>
      </div>
      <div className="md:col-span-5 md:col-start-8 reveal" style={{ transitionDelay: "150ms" }}>
        {[
          ["Enfoque", "Arquitectura editorial y atemporal"],
          ["Sectores", "Hotelería · Comercial · Vivienda"],
          ["Alcance", "Proyectos internacionales"],
          ["Método", "Concepto — Diseño — Gestión"],
        ].map(([k, v]) => (
          <div key={k} className="border-t border-[#111111] py-4 flex justify-between gap-6 text-sm md:text-base">
            <span className="uppercase tracking-widest text-xs font-semibold pt-1">{k}</span>
            <span className="font-light text-right">{v}</span>
          </div>
        ))}
        <Line />
      </div>
    </div>
  </section>
);

const SECTORS = [
  { title: "Hotelería", items: ["Hoteles", "Resorts", "Desarrollos Hoteleros"], num: "01" },
  { title: "Centros Comerciales", items: ["Retail", "Shopping Centers", "Complejos Comerciales"], num: "02" },
  { title: "Viviendas", items: ["Casas Unifamiliares", "Desarrollos Residenciales", "Proyectos Multifamiliares"], num: "03" },
];

const Sectores = () => (
  <section className="px-6 md:px-10 lg:px-16 py-24 md:py-36" data-testid="sectores-section">
    <h2 className="reveal text-[13vw] md:text-[8vw] leading-none tracking-tight uppercase font-medium mb-14">Sectores</h2>
    <Line />
    <div className="grid grid-cols-1 md:grid-cols-3">
      {SECTORS.map((s, i) => (
        <div key={s.title} className={`reveal py-10 md:py-14 md:pr-10 ${i > 0 ? "md:border-l md:border-[#111111] md:pl-10" : ""} border-b border-[#111111] md:border-b-0`} style={{ transitionDelay: `${i * 120}ms` }}>
          <span className="text-xs font-semibold text-[#D94A1E]">{s.num}</span>
          <h3 className="mt-4 text-2xl md:text-3xl uppercase tracking-tight font-medium">{s.title}</h3>
          <ul className="mt-6 space-y-2 text-base font-light text-[#111111]/80">
            {s.items.map((it) => <li key={it}>{it}</li>)}
          </ul>
        </div>
      ))}
    </div>
    <Line className="hidden md:block" />
  </section>
);

const FeaturedProjects = ({ projects }) => (
  <section className="px-6 md:px-10 lg:px-16 py-24 md:py-36" data-testid="featured-projects-section">
    <div className="flex items-end justify-between mb-14 gap-6">
      <h2 className="reveal text-[13vw] md:text-[8vw] leading-none tracking-tight uppercase font-medium">Obras</h2>
      <Link to="/obras" data-testid="featured-ver-todas-link" className="ed-link text-sm uppercase tracking-widest font-semibold mb-3 shrink-0">Ver todas</Link>
    </div>
    <Line className="mb-16" />
    <div className="space-y-24 md:space-y-36">
      {projects.slice(0, 3).map((p, i) => (
        <Link
          key={p.id}
          to={`/obras/${p.slug}`}
          data-testid={`featured-project-${p.slug}`}
          className={`reveal grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 group ${i % 2 === 1 ? "" : ""}`}
        >
          <div className={`img-zoom ${i % 2 === 0 ? "md:col-span-7" : "md:col-span-7 md:col-start-6 md:order-2"}`}>
            <img src={getImageUrl(p.hero_image)} alt={p.title} loading="lazy" className="w-full aspect-[4/3] object-cover" />
          </div>
          <div className={`flex flex-col justify-end ${i % 2 === 0 ? "md:col-span-4 md:col-start-9" : "md:col-span-4 md:col-start-1 md:order-1"}`}>
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D94A1E]">{p.sector}</span>
            <h3 className="mt-3 text-3xl md:text-4xl uppercase tracking-tight font-medium group-hover:text-[#D94A1E] transition-colors duration-300">{p.title}</h3>
            <div className="mt-6 border-t border-[#111111] pt-3 flex justify-between text-sm font-light">
              <span>{p.location}</span>
              <span>{p.year}</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const SERVICES = [
  { title: "Arquitectura", items: ["Diseño de Concepto", "Diseño Arquitectónico", "Documentación Técnica"], num: "01" },
  { title: "Diseño", items: ["Diseño Interior", "Planificación Espacial", "Experiencia a través del Espacio"], num: "02" },
  { title: "Gestión", items: ["Gestión de Proyecto", "Supervisión de Construcción", "Coordinación Técnica"], num: "03" },
];

const Servicios = () => (
  <section id="servicios" className="px-6 md:px-10 lg:px-16 py-24 md:py-36 scroll-mt-16" data-testid="servicios-section">
    <h2 className="reveal text-[13vw] md:text-[8vw] leading-none tracking-tight uppercase font-medium mb-14">Servicios</h2>
    {SERVICES.map((s, i) => (
      <div key={s.title}>
        <Line />
        <div className="reveal grid grid-cols-12 py-10 md:py-14 gap-4" style={{ transitionDelay: `${i * 100}ms` }}>
          <span className="col-span-2 md:col-span-1 text-xs font-semibold text-[#D94A1E] pt-2">{s.num}</span>
          <h3 className="col-span-10 md:col-span-5 text-3xl md:text-5xl uppercase tracking-tight font-medium">{s.title}</h3>
          <ul className="col-span-10 col-start-3 md:col-span-4 md:col-start-9 space-y-2 text-base font-light text-[#111111]/80">
            {s.items.map((it) => <li key={it}>{it}</li>)}
          </ul>
        </div>
      </div>
    ))}
    <Line />
  </section>
);

const Estudio = () => (
  <section id="estudio" className="px-6 md:px-10 lg:px-16 py-24 md:py-36 scroll-mt-16" data-testid="estudio-section">
    <h2 className="reveal text-[13vw] md:text-[8vw] leading-none tracking-tight uppercase font-medium mb-14">Estudio</h2>
    <Line className="mb-16" />
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-4 reveal img-zoom">
        <img src={PORTRAIT} alt="Sebastián Ferradás" loading="lazy" className="w-full aspect-[3/4] object-cover grayscale" data-testid="estudio-portrait" />
        <p className="mt-4 text-sm font-light">Sebastián Ferradás</p>
        <p className="text-xs text-[#111111]/60 uppercase tracking-widest mt-1">Arquitecto · Fundador</p>
      </div>
      <div className="md:col-span-6 md:col-start-6 reveal" style={{ transitionDelay: "150ms" }}>
        <p className="text-xl md:text-2xl font-light leading-snug max-w-lg mb-12">
          Una práctica construida sobre la precisión, la cultura arquitectónica y el compromiso con cada proyecto.
        </p>
        {[
          ["Especialidad", "Arquitectura e Interiorismo"],
          ["Sectores", "Hotelería · Centros Comerciales · Vivienda"],
          ["Servicios", "Arquitectura · Diseño · Gestión"],
          ["Experiencia", "Internacional"],
        ].map(([k, v]) => (
          <div key={k} className="border-t border-[#111111] py-5 grid grid-cols-2 gap-6">
            <span className="uppercase tracking-widest text-xs font-semibold pt-1">{k}</span>
            <span className="font-light text-base md:text-lg">{v}</span>
          </div>
        ))}
        <Line />
      </div>
    </div>
  </section>
);

const Contacto = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Mensaje enviado. Nos pondremos en contacto pronto.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("No se pudo enviar el mensaje. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="px-6 md:px-10 lg:px-16 py-24 md:py-36 scroll-mt-16" data-testid="contacto-section">
      <h2 className="reveal text-[11vw] md:text-[6.5vw] leading-[0.95] tracking-tight uppercase font-medium max-w-5xl mb-16">
        Construyamos algo <span className="text-[#D94A1E]">extraordinario</span>
      </h2>
      <Line className="mb-16" />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4 reveal">
          <p className="text-base font-light leading-relaxed text-[#111111]/80 max-w-xs">
            Cuéntanos sobre tu proyecto. Respondemos todas las consultas en un plazo de 48 horas.
          </p>
        </div>
        <form onSubmit={submit} className="md:col-span-6 md:col-start-7 reveal space-y-10" style={{ transitionDelay: "150ms" }} data-testid="contact-form">
          <input
            data-testid="contact-input-nombre"
            className="ed-input"
            placeholder="Nombre"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            data-testid="contact-input-email"
            className="ed-input"
            type="email"
            placeholder="Email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <textarea
            data-testid="contact-input-mensaje"
            className="ed-input resize-none"
            rows={4}
            placeholder="Mensaje"
            required
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="inline-flex items-center gap-3 text-lg uppercase tracking-wide font-medium text-[#D94A1E] hover:gap-6 transition-all duration-300 disabled:opacity-50"
          >
            {sending ? "Enviando…" : "Enviar"} <span aria-hidden>→</span>
          </button>
        </form>
      </div>
    </section>
  );
};

export default function Home() {
  const [projects, setProjects] = useState([]);
  const location = useLocation();
  const rootRef = useRevealObserver();

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects(Array.isArray(r.data) && r.data.length ? r.data : getStoredProjects()))
      .catch(() => setProjects(getStoredProjects()));
  }, []);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [location.hash]);

  return (
    <div ref={rootRef} className="bg-[#F3F0EA] text-[#111111]">
      <Menu />
      <Hero />
      <Intro />
      <Sectores />
      <FeaturedProjects projects={projects} />
      <Servicios />
      <Estudio />
      <Contacto />
      <Footer />
    </div>
  );
}
